import { supabase } from "../../lib/supabaseClient";

export const notifyCompany = async (reportData) => {
  try {
    let orgDetails;
    let modelDetails;

    // --- Start of Modified Logic ---

    // 1. Determine Organization Details
    // If new org details are provided directly, use them.
    if (reportData.new_org_details && reportData.new_org_details.email) {
      console.log("Using new organization details provided in the report.");
      orgDetails = {
        name: reportData.new_org_details.name,
        email: reportData.new_org_details.email,
      };
    }
    // Otherwise, fetch details for the existing org from the database.
    else if (reportData.org_id) {
      console.log(
        `Fetching details for existing organization ID: ${reportData.org_id}`,
      );
      const { data: org, error: orgError } = await supabase
        .from("organizations")
        .select("name, email")
        .eq("id", reportData.org_id)
        .single();

      if (orgError) {
        throw new Error(
          `Could not get organization details: ${orgError.message}`,
        );
      }
      orgDetails = org;
    }

    // 2. Determine Model Details
    // If a new model name is provided, use it.
    if (reportData.new_model_name) {
      console.log("Using new model name provided in the report.");
      modelDetails = { name: reportData.new_model_name };
    }
    // Otherwise, fetch the existing model's name from the database.
    else if (reportData.model_id) {
      console.log(
        `Fetching details for existing model ID: ${reportData.model_id}`,
      );
      const { data: model, error: modelError } = await supabase
        .from("models")
        .select("name")
        .eq("id", reportData.model_id)
        .single();

      if (modelError) {
        throw new Error(`Could not get model details: ${modelError.message}`);
      }
      modelDetails = model;
    }

    // 3. Validate that we have the necessary details before proceeding
    if (!orgDetails?.email) {
      throw new Error(
        "Target organization email is missing or could not be determined.",
      );
    }
    if (!modelDetails?.name) {
      throw new Error("Model name is missing or could not be determined.");
    }

    // --- End of Modified Logic ---

    // Prepare email data using the determined org and model details
    const emailData = {
      to: orgDetails.email.trim(),
      subject: `${reportData.report_title} [New Issue Report for ${modelDetails.name} via AISecurityPulse]`,
      attachment: reportData.attachment,
      html: `
      <!DOCTYPE html>
      <html lang="en">
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>New Report Notification</title>
      </head>
      <body style="margin: 0; padding: 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; background-color: #f4f4f7; color: #333333;">
        <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%">
          <tr>
            <td style="padding: 20px 0;">
              <table role="presentation" align="center" cellspacing="0" cellpadding="0" border="0" width="600" style="margin: 0 auto; background-color: #ffffff; border: 1px solid #e0e0e0; border-radius: 8px;">
                <tr>
                  <td style="padding: 40px 40px 20px; text-align: left;">
                    <h1 style="margin: 0; font-size: 24px; font-weight: 600;">${reportData.report_title}</h1>
                    <p style="margin: 4px 0 0; font-size: 16px; color: #555555;">A new security report for the model: <strong>${modelDetails.name}</strong></p>
                  </td>
                </tr>

                <tr>
                  <td style="padding: 0 40px;">
                    <hr style="border: 0; border-top: 1px solid #eeeeee;">
                    <h2 style="font-size: 18px; margin: 20px 0 10px;">Summary of Findings</h2>
                    <blockquote style="margin: 0; padding: 15px; background-color: #f9f9f9; border-left: 4px solid #cccccc; font-style: italic; color: #555555;">
                      <p style="margin: 0; line-height: 1.6;">${reportData.summary}</p>
                    </blockquote>
                  </td>
                </tr>

                <tr>
                  <td style="padding: 20px 40px;">
                    <hr style="border: 0; border-top: 1px solid #eeeeee; margin-bottom: 20px;">
                    <h3 style="font-size: 18px; margin: 0 0 15px;">Report Details</h3>
                    <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%">
                      <tr>
                        <td style="padding: 8px 0; font-size: 14px; color: #555555;" width="150"><strong>Report Type:</strong></td>
                        <td style="padding: 8px 0; font-size: 14px;">${reportData.type_of_report}</td>
                      </tr>
                      <tr>
                        <td style="padding: 8px 0; font-size: 14px; color: #555555;"><strong>Submission:</strong></td>
                        <td style="padding: 8px 0; font-size: 14px;">${reportData.is_anonymous ? "Anonymous" : "Public"}</td>
                      </tr>
                      <tr>
                        <td style="padding: 8px 0; font-size: 14px; color: #555555;"><strong>Reporter Email:</strong></td>
                        <td style="padding: 8px 0; font-size: 14px;">${reportData.email || "N/A"}</td>
                      </tr>
                      ${
                        reportData.attachment
                          ? `
                      <tr>
                        <td style="padding: 8px 0; font-size: 14px; color: #555555;"><strong>Attachment:</strong></td>
                        <td style="padding: 8px 0; font-size: 14px;">${reportData.attachment.filename}</td>
                      </tr>`
                          : ""
                      }
                      <tr>
                        <td style="padding: 8px 0; font-size: 14px; color: #555555;"><strong>Timestamp:</strong></td>
                        <td style="padding: 8px 0; font-size: 14px;">${new Date().toLocaleString("en-US", { timeZone: "UTC" })} UTC</td>
                      </tr>
                    </table>
                  </td>
                </tr>
                
                ${
                  reportData.is_anonymous
                    ? `
                <tr>
                  <td style="padding: 10px 40px 30px;">
                    <div style="padding: 15px; background-color: #fffbe6; border: 1px solid #ffe58f; border-radius: 4px;">
                      <p style="margin: 0; font-size: 14px; line-height: 1.5; color: #8a6d3b;">
                        <strong>Important:</strong> This report was submitted anonymously. Please respect the reporter's privacy and do not attempt to identify them publicly.
                      </p>
                    </div>
                  </td>
                </tr>`
                    : ""
                }

              </table>
            </td>
          </tr>
          <tr>
            <td style="padding: 20px; text-align: center;">
              <p style="font-size: 12px; color: #888888;">This is an automated notification from AISecurityPulse.</p>
            </td>
          </tr>
        </table>
      </body>
      </html>
      `,
    };

    const { data, error } = await supabase.functions.invoke("send-email", {
      body: emailData,
    });

    if (error) {
      console.error("Edge function error:", error);
      throw new Error("Failed to send notification email");
    }
    if (!data?.success) {
      console.error("Resend API reported failure:", data);
      throw new Error("Email was not sent successfully");
    }

    console.log("✅ Email sent successfully! Resend response:", data.result);
    return true;
  } catch (error) {
    console.error("Error notifying company:", error);
    throw error;
  }
};
